import { useEffect, useRef, useState } from 'react';

export default function CredentialsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const clients = [
    'GUCCI',
    '国立劇場',
    'ポルシェ',
    'Netflix',
    'フジテレビ',
    'ディズニー',
    '',
    '',
  ];

  const collaborators = {
    overseas: [
      { name: 'Sidi Larbi Cherkaoui', role: '演出振付家 / ベルギー' },
      { name: 'Yussef Dayes', role: 'ジャズ' },
      { name: 'Damian Jalet', role: '演出振付家' },
      { name: 'Zakir Hussain', role: 'タブラ / インド' },
    ],
    domestic: [
      { name: '森山未来', role: '俳優・ダンサー' },
      { name: '坂東玉三郎', role: '歌舞伎' },
      { name: '尾上菊之丞', role: '日本舞踊' },
      { name: '津村礼次郎', role: '能楽' },
      { name: '初音ミク × 鼓童', role: '' },
      { name: '石川さゆり × 鼓童', role: '' },
    ],
  };

  const scopes = [
    {
      title: '迎賓演奏',
      description: '国際的な場における格調高い演奏',
    },
    {
      title: '舞台演出',
      description: '伝統と革新を融合した総合演出',
    },
    {
      title: '楽曲提供',
      description: '映像作品・広告への音楽制作',
    },
    {
      title: '文化監修',
      description: 'プロジェクトの文化的真正性の担保',
    },
  ];

  return (
    <section
      id="credentials"
      ref={sectionRef}
      className="py-32 px-8 bg-washi"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`grid grid-cols-1 md:grid-cols-3 gap-12 mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative">
            <h2 className="font-serif text-5xl font-bold text-sumi tracking-wide">
              Credentials<br />& Track Record
            </h2>
          </div>
          <div className="md:col-span-2 flex items-center">
            <p className="font-sans text-lg text-sumi/80 leading-relaxed">
              世界的ブランド、国家機関、著名アーティストとの協働実績。
            </p>
          </div>
        </div>

        {/* Three Column List */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Decorative Background Circle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-indigo/5 -z-10"></div>

          {/* Scope Column */}
          <div
            className={`bg-white/80 backdrop-blur-sm rounded-lg p-8 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h3 className="font-sans text-sm font-semibold text-sumi mb-6 tracking-widest uppercase">
              Scope
            </h3>
            <div className="space-y-6">
              {scopes.map((scope, index) => (
                <div key={index}>
                  <h4 className="font-serif text-base font-semibold text-sumi mb-2">
                    {scope.title}
                  </h4>
                  <p className="font-sans text-sm text-sumi/70 leading-relaxed">
                    {scope.description}
                  </p>
                  {index < scopes.length - 1 && (
                    <div className="mt-6 border-b border-dashed border-sumi/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Collaborators Column */}
          <div
            className={`bg-white/80 backdrop-blur-sm rounded-lg p-8 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h3 className="font-sans text-sm font-semibold text-sumi mb-6 tracking-widest uppercase">
              Collaborators
            </h3>

            {/* Overseas */}
            <div className="mb-6">
              <p className="font-sans text-xs text-sumi/50 tracking-widest uppercase mb-3">海外</p>
              <div className="space-y-3">
                {collaborators.overseas.map((c, index) => (
                  <div key={index}>
                    <div className="flex items-start gap-3">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-star-fill text-indigo text-xs"></i>
                      </div>
                      <div className="flex-1">
                        <p className="font-serif text-sm text-sumi/90 leading-snug">{c.name}</p>
                        {c.role && (
                          <p className="font-sans text-xs text-sumi/50 mt-0.5">{c.role}</p>
                        )}
                      </div>
                    </div>
                    {index < collaborators.overseas.length - 1 && (
                      <div className="mt-3 border-b border-dashed border-sumi/20"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="border-b border-sumi/20 mb-6"></div>

            {/* Domestic */}
            <div>
              <p className="font-sans text-xs text-sumi/50 tracking-widest uppercase mb-3">国内</p>
              <div className="space-y-3">
                {collaborators.domestic.map((c, index) => (
                  <div key={index}>
                    <div className="flex items-start gap-3">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-star-fill text-indigo text-xs"></i>
                      </div>
                      <div className="flex-1">
                        <p className="font-serif text-sm text-sumi/90 leading-snug">{c.name}</p>
                        {c.role && (
                          <p className="font-sans text-xs text-sumi/50 mt-0.5">{c.role}</p>
                        )}
                      </div>
                    </div>
                    {index < collaborators.domestic.length - 1 && (
                      <div className="mt-3 border-b border-dashed border-sumi/20"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clients Column */}
          <div
            className={`bg-white/80 backdrop-blur-sm rounded-lg p-8 transition-all duration-1000 delay-600 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
          >
            <h3 className="font-sans text-sm font-semibold text-sumi mb-6 tracking-widest uppercase">
              Clients
            </h3>
            <div className="space-y-4">
              {clients.map((client, index) => (
                <div key={index}>
                  <p className="font-serif text-base text-sumi/80">{client}</p>
                  {index < clients.length - 1 && (
                    <div className="mt-4 border-b border-dashed border-sumi/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}